import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { errorText } from "../ui";
import { useHideTabBar } from "./TabBar";

/**
 * Shared iPad (kiosk): staff unlock with their own PIN, the device locks itself after N minutes without
 * activity (salon rule), and the server also refuses requests while locked (423) — so a stale screen can't act.
 */
export function KioskGate() {
  const { me, refresh } = useAuth();
  const k = me?.kiosk;
  useEffect(() => {
    const on = () => void refresh();
    window.addEventListener("spos:kiosk-locked", on);
    return () => window.removeEventListener("spos:kiosk-locked", on);
  }, [refresh]);
  // idle auto-lock + activity pings while unlocked
  const last = useRef(Date.now());
  const pinged = useRef(0);
  useEffect(() => {
    if (!k || k.locked) return;
    last.current = Date.now();
    const act = () => {
      last.current = Date.now();
      if (Date.now() - pinged.current > 30_000) { pinged.current = Date.now(); void api("/api/kiosk/ping", { method: "POST", body: {} }).catch(() => undefined); }
    };
    const evs = ["pointerdown", "keydown", "touchstart"] as const;
    evs.forEach((e) => window.addEventListener(e, act, { passive: true }));
    const timer = window.setInterval(() => {
      if (Date.now() - last.current > k.autoLockMinutes * 60_000) void api("/api/kiosk/lock", { method: "POST", body: {} }).finally(() => void refresh());
    }, 10_000);
    return () => { evs.forEach((e) => window.removeEventListener(e, act)); window.clearInterval(timer); };
  }, [k?.locked, k?.autoLockMinutes]);
  if (!k?.locked) return null;
  return <PinPad onUnlocked={() => void refresh()} lockoutUntil={k.lockoutUntil} />;
}

function PinPad({ onUnlocked, lockoutUntil }: { onUnlocked: () => void; lockoutUntil: string | null }) {
  useHideTabBar(true); // (Task Q) PIN entry is full-screen
  const { t } = useI18n();
  const { me } = useAuth();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState<string | null>(lockoutUntil ? t("w6.kiosk.lockout", { time: new Date(lockoutUntil).toLocaleTimeString() }) : null);
  const [busy, setBusy] = useState(false);
  const submit = async (p: string) => {
    setBusy(true);
    try { await api("/api/kiosk/unlock", { method: "POST", body: { pin: p } }); setPin(""); setErr(null); onUnlocked(); }
    catch (e) {
      setPin("");
      if (e instanceof ApiError && e.code === "bad_pin" && typeof e.details?.attemptsLeft === "number") setErr(t("w6.kiosk.wrong", { n: e.details.attemptsLeft }));
      else if (e instanceof ApiError && e.code === "kiosk_lockout" && e.details?.until) setErr(t("w6.kiosk.lockout", { time: new Date(e.details.until).toLocaleTimeString() }));
      else setErr(errorText(e));
    } finally { setBusy(false); }
  };
  const press = (d: string) => { if (busy) return; const p = (pin + d).slice(0, 6); setPin(p); };
  return (
    <div className="overlay center kiosk-lock" role="dialog" aria-modal="true" aria-label={t("w6.kiosk.locked")}>
      <div className="modal stack center-text" style={{ maxWidth: 340 }}>
        <div className="muted">{me?.membership?.tenantName}</div>
        <h2>🔒 {t("w6.kiosk.enterPin")}</h2>
        <div className="big-num md" aria-live="polite" aria-label={t("w6.kiosk.enterPin")}>{"●".repeat(pin.length) || "\u00a0"}</div>
        <div className="pinpad" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => <button key={d} className="btn btn-soft btn-xl" onClick={() => press(d)}>{d}</button>)}
          <button className="btn btn-ghost btn-xl" aria-label="⌫" onClick={() => setPin(pin.slice(0, -1))}>⌫</button>
          <button className="btn btn-soft btn-xl" onClick={() => press("0")}>0</button>
          <button className="btn btn-primary btn-xl" disabled={busy || pin.length < 4} onClick={() => void submit(pin)}>{t("w6.kiosk.unlock")}</button>
        </div>
        {err && <div className="note" role="alert">{err}</div>}
      </div>
    </div>
  );
}

/** Items for the account menu: turn a device into a shared kiosk, switch staff, leave kiosk mode. */
export function KioskMenuItems({ onDone }: { onDone: () => void }) {
  const { me, refresh } = useAuth();
  const { t } = useI18n();
  const role = me?.membership?.role;
  if (!me?.membership || role === "staff") return null;
  const k = me.kiosk;
  const call = async (path: string, body: unknown = {}) => {
    try { await api(path, { method: "POST", body }); } catch (e) { window.alert(errorText(e)); }
    onDone(); await refresh();
  };
  if (!k) return <button role="menuitem" className="menu-item" onClick={() => void call("/api/kiosk/enable")}>{t("w6.kiosk.enable")}</button>;
  return (
    <>
      <button role="menuitem" className="menu-item" onClick={() => void call("/api/kiosk/lock")}>{t("w6.kiosk.switch")}</button>
      <button role="menuitem" className="menu-item" onClick={() => {
        const manager = role === "owner" || role === "manager";
        const pw = manager ? undefined : window.prompt(t("w6.kiosk.exitPassword")) ?? undefined;
        if (!manager && !pw) return;
        void call("/api/kiosk/disable", pw ? { password: pw } : {});
      }}>{t("w6.kiosk.exit")}</button>
    </>
  );
}
